import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Spinner,
} from '../../components/UI';
import api, { callAnalysisAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineSparkles, HiOutlineCheckCircle, HiOutlineExclamation,
  HiOutlineLightBulb, HiOutlineRefresh, HiOutlineArrowLeft,
  HiOutlineFlag,
} from 'react-icons/hi';

const PROCESSING_STATES = ['pending', 'transcribing', 'analyzing'];

// ─── Marking system: shared helpers + circular score ring ────────────────────
const scoreColors = (v) => {
  if (v == null) return { stroke: '#d1d5db', text: 'text-gray-400' };
  if (v >= 80) return { stroke: '#22c55e', text: 'text-green-600' };
  if (v >= 60) return { stroke: '#f59e0b', text: 'text-amber-600' };
  return { stroke: '#ef4444', text: 'text-red-500' };
};

/**
 * Circular score ring — used for the overall mark and every parameter mark.
 * viewBox 36 with r=15.9 → circumference ≈ 100, so 1 unit = 1%.
 * Pass `onClick` to make the ring selectable (shows its remark below the row).
 */
const ScoreRing = ({ value, label, sub, size = 88, active = false, onClick }) => {
  const c = scoreColors(value);
  const filled = value != null;
  const dasharray = filled ? `${Math.max(0, Math.min(100, value))}, 100` : '0, 100';
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`flex flex-col items-center gap-1.5 focus:outline-none ${onClick ? 'cursor-pointer' : ''}`}
      title={filled ? `${label}: ${value}%` : `${label}: not assessed in this call`}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3.4" />
          {filled && (
            <circle
              cx="18" cy="18" r="15.9" fill="none"
              strokeWidth="3.4"
              strokeLinecap="round"
              strokeDasharray={dasharray}
              stroke={c.stroke}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-bold leading-none ${c.text}`} style={{ fontSize: Math.round(size * 0.24) }}>
            {filled ? `${value}%` : 'N/A'}
          </span>
          {sub && (
            <span className="text-[10px] text-gray-400 uppercase tracking-wide mt-0.5" style={{ fontSize: Math.max(9, Math.round(size * 0.12)) }}>
              {sub}
            </span>
          )}
        </div>
        {active && <div className={`absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full ${filled ? '' : 'bg-gray-300'}`} style={filled ? { backgroundColor: c.stroke } : {}} />}
      </div>
      <span className={`text-[11px] leading-tight text-center max-w-[96px] ${active ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
        {label}
      </span>
    </Tag>
  );
};

const CallAnalysisDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [audioUrl, setAudioUrl] = useState(null);
  const [retrying, setRetrying] = useState(false);
  // Marking system: which parameter circle is selected (shows its remark below)
  const [selectedParam, setSelectedParam] = useState(null);

  // Fetch analysis; poll every 3s while processing
  const { data: analysis, isLoading } = useQuery({
    queryKey: ['call-analysis', id],
    queryFn: async () => {
      const response = await callAnalysisAPI.getById(id);
      return response.data?.data || {};
    },
    refetchInterval: (query) =>
      PROCESSING_STATES.includes(query.state.data?.status) ? 3000 : false,
  });

  const status = analysis?.status;

  // Authenticated audio fetch (blob) when processing completes
  useEffect(() => {
    if (status !== 'completed' && status !== 'failed') return;
    let url = null;
    const fetchAudio = async () => {
      try {
        const response = await api.get(`/recordings/file/${analysis.recordingId?._id}`, {
          responseType: 'blob',
        });
        url = URL.createObjectURL(response.data);
        setAudioUrl(url);
      } catch (error) {
        console.error('Failed to load audio:', error);
      }
    };
    if (analysis.recordingId?._id) fetchAudio();
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [status, analysis?.recordingId?._id]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await callAnalysisAPI.retry(id);
      toast.success('Analysis re-queued');
      queryClient.invalidateQueries({ queryKey: ['call-analysis', id] });
      queryClient.invalidateQueries({ queryKey: ['call-analysis'] });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to retry analysis');
    } finally {
      setRetrying(false);
    }
  };

  // Re-run ONLY the marking (percentage) on the stored transcript —
  // used for analyses completed before the marking system existed
  const [rescoring, setRescoring] = useState(false);
  const handleRescore = async () => {
    setRescoring(true);
    try {
      await callAnalysisAPI.rescore(id);
      toast.success('Re-scoring started — marks will appear in a moment');
      queryClient.invalidateQueries({ queryKey: ['call-analysis', id] });
      queryClient.invalidateQueries({ queryKey: ['call-analysis'] });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start re-scoring');
    } finally {
      setRescoring(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // ─── Marking system data ─────────────────────────────────────────────────
  const overallScore = analysis?.score?.overall;
  const scoreParams = analysis?.score?.parameters || [];

  // ─── Customer Satisfaction Score + deal-risk flags ──────────────────────
  const satisfactionScore = analysis?.analysis?.satisfactionScore;
  const dealRisk = analysis?.analysis?.dealRisk || {};

  // ─── Sentiment trend across the customer's analyzed calls ───────────────
  // One number: the customer's average satisfaction/sentiment across every
  // analyzed call (by lead, or by phone number), shown as a circle below.
  const trend = analysis?.sentimentTrend;
  const trendHistory = trend?.history || [];

  // Deal-risk badge: how likely the AI thinks this deal is lost.
  const RISK_STYLES = {
    low: { label: 'Low Risk', cls: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
    medium: { label: 'At Risk', cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
    high: { label: 'At Risk', cls: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
    critical: { label: 'Critical Risk', cls: 'bg-red-600 text-white', dot: 'bg-red-700' },
  };
  const risk = RISK_STYLES[dealRisk.level];

  const recording = analysis?.recordingId || {};

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex justify-center py-24"><Spinner size="lg" /></div>
      </Layout>
    );
  }

  if (!analysis?._id) {
    return (
      <Layout>
        <div className="p-6">
          <Card className="p-10 text-center">
            <p className="text-gray-500">Call analysis not found.</p>
            <Button className="mt-4" onClick={() => navigate('/app/call-analysis')}>Back to list</Button>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <button
              onClick={() => navigate('/app/call-analysis')}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-1"
            >
              <HiOutlineArrowLeft className="w-4 h-4" /> Call Analysis
            </button>
            <h1 className="text-xl font-bold text-gray-900 truncate">
              {recording.originalFileName || recording.fileName || 'Call Recording'}
            </h1>
            <p className="text-sm text-gray-500">
              {recording.phoneNumber && `☎ ${recording.phoneNumber} · `}
              {analysis.language?.toUpperCase()} · {formatTime(analysis.transcript?.durationSeconds || 0)}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-shrink-0">
            {analysis.status === 'failed' && (
              <Button onClick={handleRetry} loading={retrying} className="flex items-center gap-2">
                <HiOutlineRefresh className="w-4 h-4" /> Retry
              </Button>
            )}
            {analysis.status === 'completed' && (overallScore == null || satisfactionScore == null) && (
              <Button
                onClick={handleRescore}
                loading={rescoring}
                className="flex items-center gap-2 whitespace-nowrap"
                title="Re-run the AI on the stored transcript — adds satisfaction score, interest level and deal-risk flags to analyses completed before they existed"
              >
                <HiOutlineSparkles className="w-4 h-4" /> Recalculate Marks
              </Button>
            )}
          </div>
        </div>

        {/* Processing state */}
        {PROCESSING_STATES.includes(status) && (
          <Card className="p-10 text-center">
            <Spinner size="lg" />
            <p className="mt-4 text-gray-600 font-medium">
              {status === 'transcribing' ? 'Transcribing audio with speaker detection…' : status === 'analyzing' ? 'AI is analyzing the conversation…' : 'Queued…'}
            </p>
            <p className="text-xs text-gray-400 mt-1">This page updates automatically. Long recordings can take a few minutes.</p>
          </Card>
        )}

        {/* Failed state */}
        {status === 'failed' && (
          <Card className="p-6 border border-red-200 bg-red-50">
            <div className="flex items-start gap-3">
              <HiOutlineExclamation className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800">Analysis failed</h3>
                <p className="text-sm text-red-700 mt-1 break-words">{analysis.error || 'Unknown error'}</p>
                <Button size="sm" className="mt-3" onClick={handleRetry} loading={retrying}>
                  <HiOutlineRefresh className="w-4 h-4" /> Try again
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Completed state */}
        {status === 'completed' && (
          <>
            {/* Audio player */}
            {audioUrl && (
              <Card>
                <audio controls src={audioUrl} className="w-full" controlsList="" />
              </Card>
            )}

            {/* Summary + sentiment */}
            <Card>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Summary</h2>
                  <p className="text-gray-800 mt-1">{analysis.analysis?.summary || '—'}</p>
                </div>
                {analysis.analysis?.customerSentiment && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                    analysis.analysis.customerSentiment === 'positive' ? 'bg-green-100 text-green-700'
                      : analysis.analysis.customerSentiment === 'negative' ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {analysis.analysis.customerSentiment}
                  </span>
                )}
              </div>
              {(analysis.analysis?.keyTopics || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {analysis.analysis.keyTopics.map((topic, i) => (
                    <span key={i} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs">{topic}</span>
                  ))}
                </div>
              )}
            </Card>

            {/* Marking system — all circular marks in ONE line, positioned
                below the recording and above the Pros/Cons/Suggestions. The
                Customer Satisfaction ring comes after Closing & Next Steps —
                it scores the CUSTOMER, not the agent — and the Sentiment
                Trend ring (customer's average across their calls) is last. */}
            {(overallScore != null || satisfactionScore != null || dealRisk.level || trend?.avgScore != null) && (
              <Card>
                <div className="flex flex-wrap justify-center items-start gap-x-3 gap-y-5 sm:gap-x-5">
                  {/* Overall mark — bigger circle */}
                  {overallScore != null && (
                    <>
                      <ScoreRing
                        value={overallScore}
                        label="Overall"
                        sub={analysis.score?.grade || undefined}
                        size={104}
                      />
                      <div className="hidden sm:block w-px h-20 bg-gray-200 self-center mx-1" />
                    </>
                  )}
                  {/* Every parameter mark as a circle — click to see the remark */}
                  {scoreParams.map((p) => (
                    <ScoreRing
                      key={p.key}
                      value={p.score}
                      label={p.label}
                      size={86}
                      active={selectedParam === p.key}
                      onClick={() => setSelectedParam(p.key === selectedParam ? null : p.key)}
                    />
                  ))}
                  {/* Customer Satisfaction Score — LAST circle, after Closing &
                      Next Steps. Click to see the AI's justification below. */}
                  {satisfactionScore != null && (
                    <ScoreRing
                      value={satisfactionScore}
                      label="Customer Satisfaction"
                      sub={satisfactionScore >= 80 ? 'Very Interested' : satisfactionScore >= 60 ? 'Interested' : satisfactionScore >= 40 ? 'Undecided' : 'Not Interested'}
                      size={86}
                      active={selectedParam === 'satisfaction'}
                      onClick={() => setSelectedParam(selectedParam === 'satisfaction' ? null : 'satisfaction')}
                    />
                  )}
                  {/* Sentiment Trend — the ONLY extra circle: the customer's
                      AVERAGE sentiment across all their analyzed calls (by
                      lead, or by phone number). Click to see the breakdown. */}
                  {trend?.avgScore != null && (
                    <ScoreRing
                      value={trend.avgScore}
                      label="Sentiment Trend"
                      sub={trend.direction
                        ? (trend.direction === 'improving' ? 'Improving' : trend.direction === 'declining' ? 'Declining' : 'Stable')
                        : undefined}
                      size={86}
                      active={selectedParam === 'trend'}
                      onClick={() => setSelectedParam(selectedParam === 'trend' ? null : 'trend')}
                    />
                  )}
                </div>

                {/* Remark of the selected parameter circle */}
                {(() => {
                  if (selectedParam === 'trend') {
                    const dirText = trend.direction === 'improving' ? 'and improving'
                      : trend.direction === 'declining' ? 'and declining' : 'and stable';
                    return (
                      <p className="text-xs text-gray-500 text-center mt-4 pt-3 border-t max-w-2xl mx-auto">
                        <span className="font-semibold text-gray-700">Sentiment Trend</span>
                        {trend.avgScore != null && <span className="text-gray-400"> · {trend.avgScore}%</span>}
                        {' — '}
                        {`Average customer sentiment across ${trendHistory.length} analyzed call${trendHistory.length === 1 ? '' : 's'} of this customer, ${dirText}. Scores come from each call's satisfaction score (or its positive/neutral/negative sentiment when no score exists).`}
                      </p>
                    );
                  }
                  if (selectedParam === 'satisfaction') {
                    return (
                      <p className="text-xs text-gray-500 text-center mt-4 pt-3 border-t max-w-2xl mx-auto">
                        <span className="font-semibold text-gray-700">Customer Satisfaction</span>
                        {satisfactionScore != null && <span className="text-gray-400"> · {satisfactionScore}%</span>}
                        {' — '}
                        {analysis.analysis?.satisfactionRemark
                          || 'How interested and satisfied the customer sounded in this conversation — not part of the agent marks.'}
                      </p>
                    );
                  }
                  const sel = scoreParams.find((p) => p.key === selectedParam);
                  if (!sel) return null;
                  return (
                    <p className="text-xs text-gray-500 text-center mt-4 pt-3 border-t max-w-2xl mx-auto">
                      <span className="font-semibold text-gray-700">{sel.label}</span>
                      {sel.score != null && <span className="text-gray-400"> · {sel.score}%</span>}
                      {' — '}
                      {sel.remark
                        || (sel.score == null
                          ? 'Could not be assessed in this call — excluded from the overall marks.'
                          : 'No remark provided.')}
                    </p>
                  );
                })()}

                {/* At-risk deal flags — the AI heard warning signs in the call */}
                {dealRisk.level && (
                  <div className={`rounded-lg p-3 mt-4 ${
                    dealRisk.level === 'critical' ? 'bg-red-50' : dealRisk.level === 'high' ? 'bg-red-50'
                      : dealRisk.level === 'medium' ? 'bg-amber-50' : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <HiOutlineFlag className={`w-4 h-4 flex-shrink-0 ${
                        dealRisk.level === 'high' || dealRisk.level === 'critical' ? 'text-red-500'
                          : dealRisk.level === 'medium' ? 'text-amber-500' : 'text-gray-400'
                      }`} />
                      <span className={`text-sm font-semibold ${risk?.cls.split(' ')[1] || 'text-gray-600'}`}>
                        {risk?.label || 'Deal Risk'}
                      </span>
                      {dealRisk.level !== 'low' && (
                        <span className="text-xs text-gray-400 uppercase tracking-wide">{dealRisk.level} risk</span>
                      )}
                    </div>
                    {(dealRisk.signals || []).length > 0 && (
                      <ul className="space-y-1.5">
                        {dealRisk.signals.map((signal, i) => (
                          <li key={i} className="text-sm text-gray-600 flex gap-2">
                            <span className={`font-bold flex-shrink-0 ${
                              dealRisk.level === 'high' || dealRisk.level === 'critical' ? 'text-red-500'
                                : dealRisk.level === 'medium' ? 'text-amber-500' : 'text-gray-400'
                            }`}>⚠</span> {signal}
                          </li>
                        ))}
                      </ul>
                    )}
                    {dealRisk.remark && (
                      <p className="text-xs text-gray-500 mt-2">{dealRisk.remark}</p>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* Pros / Cons / Suggestions — restricted to 2–5 important points each
                (slice(0, 5) also caps analyses saved before the backend restriction) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="border-t-4 border-t-green-500">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <HiOutlineCheckCircle className="w-5 h-5 text-green-600" /> Pros
                </h3>
                <ul className="space-y-2">
                  {(analysis.analysis?.pros || []).slice(0, 5).map((item, i) => (
                    <li key={i} className="text-sm text-gray-600 flex gap-2">
                      <span className="text-green-500 font-bold">•</span> {item}
                    </li>
                  ))}
                  {!(analysis.analysis?.pros || []).length && <li className="text-sm text-gray-400">None found</li>}
                </ul>
              </Card>
              <Card className="border-t-4 border-t-red-400">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <HiOutlineExclamation className="w-5 h-5 text-red-500" /> Cons
                </h3>
                <ul className="space-y-2">
                  {(analysis.analysis?.cons || []).slice(0, 5).map((item, i) => (
                    <li key={i} className="text-sm text-gray-600 flex gap-2">
                      <span className="text-red-400 font-bold">✕</span> {item}
                    </li>
                  ))}
                  {!(analysis.analysis?.cons || []).length && <li className="text-sm text-gray-400">None detected</li>}
                </ul>
              </Card>
              <Card className="border-t-4 border-t-indigo-500">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <HiOutlineLightBulb className="w-5 h-5 text-indigo-500" /> Suggestions
                </h3>
                <ul className="space-y-2">
                  {(analysis.analysis?.suggestions || []).slice(0, 5).map((item, i) => (
                    <li key={i} className="text-sm text-gray-600 flex gap-2">
                      <span className="text-indigo-400 font-bold">→</span> {item}
                    </li>
                  ))}
                  {!(analysis.analysis?.suggestions || []).length && <li className="text-sm text-gray-400">None</li>}
                </ul>
              </Card>
            </div>

            {/* Speaking metrics */}
            {(analysis.speakingMetrics?.talkTime || []).length > 0 && (
              <Card>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Talk Time</h3>
                <div className="space-y-2">
                  {analysis.speakingMetrics.talkTime.map((t) => (
                    <div key={t.speaker} className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 w-20">{`Speaker ${t.speaker + 1}`}</span>
                      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${t.speaker === 0 ? 'bg-indigo-500' : 'bg-emerald-500'}`}
                          style={{ width: `${t.percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-24 text-right">{t.percentage}% · {formatTime(t.seconds)}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {analysis.speakingMetrics.questionsAsked} question(s) asked · {analysis.speakingMetrics.agentQuestions} by agent
                </p>
              </Card>
            )}

            {/* Dialogue transcript */}
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Transcript</h3>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <HiOutlineSparkles className="w-3.5 h-3.5" /> {analysis.stt?.model || 'deepgram'} · {analysis.transcript?.speakerCount} speakers
                </span>
              </div>
              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {(analysis.transcript?.dialogue || []).map((turn, i) => (
                  <div key={i} className="flex gap-3">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded self-start flex-shrink-0 ${turn.speaker === 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {turn.label}
                    </span>
                    <div>
                      <p className="text-sm text-gray-700">{turn.text}</p>
                      <span className="text-[10px] text-gray-400">{formatTime(turn.start)}</span>
                    </div>
                  </div>
                ))}
                {!(analysis.transcript?.dialogue || []).length && (
                  <p className="text-sm text-gray-400">No transcript available.</p>
                )}
              </div>
              {analysis.llm?.provider && (
                <p className="text-[10px] text-gray-400 mt-3 border-t pt-2">
                  Analyzed by {analysis.llm.provider} ({analysis.llm.model})
                </p>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default CallAnalysisDetail;